import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import PageHelpLink from "../../Components/Common/PageHelpLink";
import SendApprovalRejectModal from "../../Components/Common/SendApprovalRejectModal";
import { helpUrlForRoute } from "../../constants/helpRouteMap";
import {
  CampaignSend,
  CampaignSendStatus,
  approveCampaignSend,
  getSends,
  getPendingApprovals,
  rejectCampaignSend,
} from "../../helpers/sends_helper";
import {
  formatCircuitAutoResumeCountdown,
  hasCircuitAutoResumeEta,
} from "../../helpers/circuitAutoResumeEta";
import { useCircuitAutoResumeNowMs } from "../../helpers/useCircuitAutoResumeNowMs";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../helpers/tenantTimezone";
import { canApproveSend } from "../../utils/tenantRoles";

const PER_PAGE = 20;
/** Poll enquanto há pausados por circuit — detecta auto-retomada sem abrir detalhe. */
const LIST_POLL_MS = 30000;

const STATUS_FILTERS: Array<"" | CampaignSendStatus> = [
  "",
  "paused",
  "sending",
  "completed",
  "pending_approval",
  "failed",
];

const statusColor = (
  send: CampaignSend
): string => {
  if (send.status === "paused") {
    if (
      (send.pause_reason === "circuit" || send.pause_reason === "platform") &&
      send.needs_sa_resume
    ) {
      return "danger";
    }
    if (hasCircuitAutoResumeEta(send)) {
      return "warning";
    }
    return "warning";
  }
  switch (send.status) {
    case "completed":
      return "success";
    case "sending":
    case "preparing":
    case "queued":
      return "info";
    case "failed":
    case "rejected":
      return "danger";
    case "pending_approval":
      return "warning";
    case "cancelled":
      return "secondary";
    default:
      return "secondary";
  }
};

/**
 * Histórico de disparos do tenant (GET /sends) — DOC 09 § 6.1 · 13 · LAC-194+.
 */
const SendsList = () => {
  const { t, i18n } = useTranslation();
  document.title = t("sending.list.documentTitle");

  const helpHref = helpUrlForRoute("/sends", i18n.language);
  const locale = i18n.language || "pt-BR";
  const tenantTimezone = useMemo(() => readDisplayTimezone(), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const pendingMode = searchParams.get("pending");
  const pendingMine = pendingMode === "mine";
  const statusFilter = (searchParams.get("status") || "") as
    | ""
    | CampaignSendStatus;

  const [sends, setSends] = useState<CampaignSend[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvalBusyId, setApprovalBusyId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<CampaignSend | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Snapshot anterior para toast de retomada / nova pausa (in-app leve).
  const prevByIdRef = useRef<Map<number, CampaignSend>>(new Map());
  const pollReadyRef = useRef(false);

  const listEtaActive = useMemo(
    () => sends.some((row) => hasCircuitAutoResumeEta(row)),
    [sends]
  );
  const listPollActive = useMemo(
    () =>
      listEtaActive ||
      sends.some(
        (row) =>
          row.status === "paused" &&
          (row.pause_reason === "circuit" || row.pause_reason === "platform")
      ) ||
      sends.some((row) =>
        ["sending", "preparing", "queued"].includes(row.status)
      ),
    [sends, listEtaActive]
  );
  const etaNowMs = useCircuitAutoResumeNowMs(listEtaActive);

  const formatTenantDate = (iso: string | null | undefined) =>
    formatInTenantTimezone(iso, tenantTimezone, locale);

  const applySnapshotDiff = useCallback(
    (nextRows: CampaignSend[]) => {
      const prev = prevByIdRef.current;
      if (pollReadyRef.current && prev.size > 0) {
        for (const row of nextRows) {
          const before = prev.get(row.id);
          if (!before) {
            continue;
          }
          // Pausado → enviando = auto-retomada (ou SA).
          if (
            before.status === "paused" &&
            (before.pause_reason === "circuit" ||
              before.pause_reason === "platform") &&
            row.status === "sending"
          ) {
            toast.success(
              t("sending.list.toast.autoResumed", {
                subject: row.subject || `#${row.id}`,
              })
            );
          }
          // Enviando → pausado = notificação in-app leve.
          if (
            before.status === "sending" &&
            row.status === "paused" &&
            (row.pause_reason === "circuit" ||
              row.pause_reason === "platform")
          ) {
            toast.warning(
              t(
                row.needs_sa_resume
                  ? "sending.list.toast.pausedNeedsSa"
                  : "sending.list.toast.pausedMayAuto",
                { subject: row.subject || `#${row.id}` }
              )
            );
          }
        }
      }
      const map = new Map<number, CampaignSend>();
      for (const row of nextRows) {
        map.set(row.id, row);
      }
      prevByIdRef.current = map;
      pollReadyRef.current = true;
    },
    [t]
  );

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const res = pendingMine
          ? await getPendingApprovals({ page, per_page: PER_PAGE })
          : await getSends({
              page,
              per_page: PER_PAGE,
              status: statusFilter || undefined,
            });
        if (!res?.success) {
          setError(t("sending.error.load"));
          setSends([]);
          return;
        }
        const rows = res.data || [];
        applySnapshotDiff(rows);
        setSends(rows);
        setTotal(res.meta?.total ?? 0);
        setLastPage(res.meta?.last_page ?? 1);
      } catch (e: any) {
        setError(e?.message || t("sending.error.load"));
        setSends([]);
      } finally {
        if (!opts?.silent) {
          setLoading(false);
        }
      }
    },
    [page, pendingMine, statusFilter, t, applySnapshotDiff]
  );

  useEffect(() => {
    pollReadyRef.current = false;
    prevByIdRef.current = new Map();
    void load();
  }, [load]);

  // Poll silencioso enquanto há envios em voo / pausados por proteção.
  useEffect(() => {
    if (pendingMine || !listPollActive) {
      return;
    }
    const timerId = window.setInterval(() => {
      void load({ silent: true });
    }, LIST_POLL_MS);
    return () => window.clearInterval(timerId);
  }, [pendingMine, listPollActive, load]);

  const setStatusFilter = (next: string) => {
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (next) {
      params.set("status", next);
    } else {
      params.delete("status");
    }
    params.delete("pending");
    setSearchParams(params, { replace: true });
  };

  const approve = async (send: CampaignSend) => {
    if (!window.confirm(t("sending.approval.approveConfirm"))) return;
    setApprovalBusyId(send.id);
    try {
      const res = await approveCampaignSend(send.id);
      setSends((rows) =>
        rows.map((row) => (row.id === send.id ? { ...row, ...res.data } : row))
      );
      toast.success(res.message || t("sending.approval.approved"));
    } catch (e: any) {
      toast.error(e?.message || t("sending.approval.error"));
    } finally {
      setApprovalBusyId(null);
    }
  };

  const reject = async (send: CampaignSend) => {
    setApprovalBusyId(send.id);
    try {
      const res = await rejectCampaignSend(send.id, rejectionReason);
      setSends((rows) =>
        rows.map((row) => (row.id === send.id ? { ...row, ...res.data } : row))
      );
      setRejectTarget(null);
      setRejectionReason("");
      toast.success(res.message || t("sending.approval.rejected"));
    } catch (e: any) {
      toast.error(e?.message || t("sending.approval.error"));
    } finally {
      setApprovalBusyId(null);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("sending.list.title")}
            actions={
              <PageHelpLink href={helpHref} label={t("sending.list.help")} />
            }
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <h5 className="card-title mb-0">{t("sending.list.title")}</h5>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {!pendingMine ? (
                      <Input
                        type="select"
                        bsSize="sm"
                        style={{ maxWidth: 200 }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        aria-label={t("sending.list.filter.statusLabel")}
                      >
                        {STATUS_FILTERS.map((value) => (
                          <option key={value || "all"} value={value}>
                            {value
                              ? t(`sending.status.${value}`)
                              : t("sending.list.filter.all")}
                          </option>
                        ))}
                      </Input>
                    ) : null}
                    <Button color="soft-primary" tag={Link} to="/campaigns">
                      {t("sending.list.goCampaigns")}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {error && (
                    <Alert color="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}
                  {pendingMine && !loading ? (
                    <Alert color="warning" className="mb-3">
                      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                        <div>
                          <strong>{t("sending.pendingMine.title")}</strong>
                          <div>
                            {t("sending.pendingMine.description", {
                              count: total,
                            })}
                          </div>
                        </div>
                        <Button
                          color="warning"
                          outline
                          size="sm"
                          tag={Link}
                          to="/sends"
                        >
                          {t("sending.pendingMine.viewAll")}
                        </Button>
                      </div>
                    </Alert>
                  ) : null}
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : sends.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                          <i className="ri-send-plane-line" />
                        </div>
                      </div>
                      <h5 className="mb-2">
                        {pendingMine
                          ? t("sending.pendingMine.empty.title")
                          : statusFilter
                            ? t("sending.list.emptyFilter.title")
                            : t("sending.list.empty.title")}
                      </h5>
                      <p className="text-muted mb-3">
                        {pendingMine
                          ? t("sending.pendingMine.empty.description")
                          : statusFilter
                            ? t("sending.list.emptyFilter.description")
                            : t("sending.list.empty.description")}
                      </p>
                      {statusFilter ? (
                        <Button
                          color="soft-primary"
                          onClick={() => setStatusFilter("")}
                        >
                          {t("sending.list.emptyFilter.cta")}
                        </Button>
                      ) : (
                        <Button color="primary" tag={Link} to="/campaigns">
                          {pendingMine
                            ? t("sending.pendingMine.empty.cta")
                            : t("sending.list.empty.cta")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-muted mb-3">
                        {t("sending.list.totalCount", { count: total })}
                      </p>
                      <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("sending.list.table.campaign")}</th>
                              <th>{t("sending.list.table.subject")}</th>
                              <th>{t("sending.list.table.status")}</th>
                              <th>{t("sending.list.table.sent")}</th>
                              <th>{t("sending.list.table.opened")}</th>
                              <th>{t("sending.list.table.startedAt")}</th>
                              <th style={{ width: 120 }} />
                            </tr>
                          </thead>
                          <tbody>
                            {sends.map((send) => (
                              <tr key={send.id}>
                                <td className="fw-medium">
                                  {send.campaign?.name ||
                                    t("sending.list.campaignFallback", {
                                      id: send.campaign_id,
                                    })}
                                  {send.is_test && (
                                    <Badge
                                      color="secondary"
                                      className="ms-2"
                                      pill
                                    >
                                      {t("sending.badge.test")}
                                    </Badge>
                                  )}
                                  {send.audience?.partial_sample && (
                                    <Badge color="info" className="ms-2" pill>
                                      {t("sending.badge.partial")}
                                    </Badge>
                                  )}
                                  {send.audience?.continuation_of_send_id ? (
                                    <Badge
                                      color="secondary"
                                      className="ms-2"
                                      pill
                                      title={t(
                                        "sending.badge.continuationHint",
                                        {
                                          id: send.audience
                                            .continuation_of_send_id,
                                        }
                                      )}
                                    >
                                      {t("sending.badge.continuation")}
                                    </Badge>
                                  ) : null}
                                  {send.pause_reason === "deadline" && (
                                    <Badge
                                      color="warning"
                                      className="ms-2"
                                      pill
                                    >
                                      {t("sending.badge.deadline")}
                                    </Badge>
                                  )}
                                  {pendingMine ? (
                                    <div className="small text-muted mt-1">
                                      {t("sending.approval.rowRequestedBy", {
                                        name:
                                          send.created_by?.name ||
                                          t(
                                            "sending.approval.rowRequestedByUnknown"
                                          ),
                                      })}
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  <div>{send.subject || "—"}</div>
                                  {pendingMine ? (
                                    <div className="small text-muted mt-1">
                                      {send.scheduled_at
                                        ? t("sending.approval.rowScheduled", {
                                            when: formatTenantDate(
                                              send.scheduled_at
                                            ),
                                          })
                                        : t(
                                            "sending.approval.rowScheduledNow"
                                          )}
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  <Badge
                                    color={statusColor(send)}
                                    className="text-uppercase"
                                  >
                                    {t(`sending.status.${send.status}`)}
                                  </Badge>
                                  {hasCircuitAutoResumeEta(send) ? (
                                    <div
                                      className="small text-muted mt-1 text-wrap"
                                      style={{ maxWidth: 220 }}
                                      title={
                                        send.circuit_auto_resume_eta_at
                                          ? formatTenantDate(
                                              send.circuit_auto_resume_eta_at
                                            )
                                          : undefined
                                      }
                                    >
                                      {formatCircuitAutoResumeCountdown(
                                        send.circuit_auto_resume_eta_at,
                                        send.needs_sa_resume,
                                        etaNowMs,
                                        t,
                                        "list"
                                      )}
                                    </div>
                                  ) : send.status === "paused" &&
                                    (send.pause_reason === "circuit" ||
                                      send.pause_reason === "platform") &&
                                    send.needs_sa_resume ? (
                                    <div
                                      className="small text-danger mt-1 text-wrap"
                                      style={{ maxWidth: 220 }}
                                    >
                                      {t("sending.list.circuitPaused.needsSa")}
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  {send.sent}
                                  <span className="text-muted small">
                                    {" "}
                                    / {send.queued}
                                  </span>
                                </td>
                                <td>{send.opened}</td>
                                <td className="text-muted">
                                  {send.started_at
                                    ? new Date(
                                        send.started_at
                                      ).toLocaleString()
                                    : send.created_at
                                      ? new Date(
                                          send.created_at
                                        ).toLocaleString()
                                      : "—"}
                                </td>
                                <td className="text-end">
                                  <div className="d-flex justify-content-end gap-1">
                                    {send.status === "pending_approval" &&
                                    canApproveSend() ? (
                                      <>
                                        <Button
                                          color="soft-success"
                                          size="sm"
                                          disabled={approvalBusyId !== null}
                                          onClick={() => void approve(send)}
                                        >
                                          {t("sending.approval.approve")}
                                        </Button>
                                        <Button
                                          color="soft-danger"
                                          size="sm"
                                          disabled={approvalBusyId !== null}
                                          onClick={() => {
                                            setRejectTarget(send);
                                            setRejectionReason("");
                                          }}
                                        >
                                          {t("sending.approval.reject")}
                                        </Button>
                                      </>
                                    ) : null}
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      tag={Link}
                                      to={`/sends/${send.id}`}
                                    >
                                      {t("sending.list.view")}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {lastPage > 1 && (
                        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                          <span className="text-muted small">
                            {t("sending.list.pagination.label", {
                              page,
                              lastPage,
                              total,
                            })}
                          </span>
                          <div className="d-flex gap-2">
                            <Button
                              color="light"
                              size="sm"
                              disabled={page <= 1}
                              onClick={() =>
                                setPage((p) => Math.max(1, p - 1))
                              }
                            >
                              {t("sending.list.pagination.prev")}
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              disabled={page >= lastPage}
                              onClick={() =>
                                setPage((p) => Math.min(lastPage, p + 1))
                              }
                            >
                              {t("sending.list.pagination.next")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <SendApprovalRejectModal
        isOpen={rejectTarget !== null}
        busy={approvalBusyId !== null}
        reason={rejectionReason}
        onReasonChange={setRejectionReason}
        onClose={() => {
          setRejectTarget(null);
          setRejectionReason("");
        }}
        onConfirm={() => {
          if (rejectTarget) {
            void reject(rejectTarget);
          }
        }}
      />
      <ToastContainer closeButton={false} limit={3} />
    </React.Fragment>
  );
};

export default SendsList;
